import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';

export interface GeolocationData {
    country: string;
    country_code: string;
    country_name: string;
    city: string;
    region: string;
    ip: string;
}

@Injectable({
    providedIn: 'root'
})
export class GeolocationService {
    private readonly TIMEOUT_MS = 10000; // Increased timeout to 10 seconds
    private cachedLocation: GeolocationData | null = null;

    constructor(private http: HttpClient) { }

    /**
     * Get visitor's country information based on IP address
     */
    getCountryByIP(): Observable<GeolocationData> {
        // Check for manual override first
        const manualOverride = this.getManualLocationOverride();
        if (manualOverride) {
            console.log('Using manual location override:', manualOverride);
            this.cachedLocation = manualOverride;
            return of(manualOverride);
        }

        // Return cached result if available
        if (this.cachedLocation) {
            console.log('Using cached location:', this.cachedLocation);
            return of(this.cachedLocation);
        }

        console.log('Fetching location from primary service (ipapi.co)...');
        // Primary service: ipapi.co (free tier, 1000 requests/day) - using fetch to bypass interceptors
        return this.getLocationWithFetch('https://ipapi.co/json/', 'primary')
            .pipe(
                map(response => {
                    console.log('Primary service response:', response);
                    console.log('Response country_code:', response.country_code);
                    console.log('Response country_name:', response.country_name);
                    console.log('Response IP:', response.ip);

                    const locationData: GeolocationData = {
                        country: response.country,
                        country_code: response.country_code,
                        country_name: response.country_name,
                        city: response.city,
                        region: response.region,
                        ip: response.ip
                    };
                    this.cachedLocation = locationData; // Cache the result
                    console.log('Primary service - processed location:', locationData);
                    return locationData;
                }),
                catchError((error) => {
                    console.error('Primary location service failed:', error);
                    return this.getFallbackLocation();
                })
            );
    }

    /**
     * Generic method to fetch location data using native fetch to bypass interceptors
     */
    private getLocationWithFetch(url: string, serviceName: string): Observable<any> {
        return new Observable(observer => {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), this.TIMEOUT_MS);

            fetch(url, {
                signal: controller.signal,
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                }
            })
                .then(response => {
                    clearTimeout(timeoutId);
                    console.log(`${serviceName} service response status:`, response.status);
                    if (!response.ok) {
                        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                    }
                    return response.json();
                })
                .then(data => {
                    observer.next(data);
                    observer.complete();
                })
                .catch(error => {
                    clearTimeout(timeoutId);
                    console.error(`${serviceName} service failed:`, error);
                    observer.error(error);
                });
        });
    }

    /**
     * Fallback method using ip-api.com (free, 1000 requests/hour) - using fetch to bypass interceptors
     */
    private getFallbackLocation(): Observable<GeolocationData> {
        console.log('Trying fallback service (ip-api.com)...');
        return this.getLocationWithFetch('https://ip-api.com/json/', 'fallback')
            .pipe(
                map(response => {
                    console.log('Fallback service response:', response);
                    const locationData: GeolocationData = {
                        country: response.country,
                        country_code: response.countryCode,
                        country_name: response.country,
                        city: response.city,
                        region: response.regionName,
                        ip: response.query
                    };
                    this.cachedLocation = locationData;
                    console.log('Fallback service - processed location:', locationData);
                    return locationData;
                }),
                catchError((error) => {
                    console.error('Fallback location service failed:', error);
                    return this.getSecondFallbackLocation();
                })
            );
    }

    /**
     * Second fallback method using ipinfo.io (free tier) - using fetch to bypass interceptors
     */
    private getSecondFallbackLocation(): Observable<GeolocationData> {
        console.log('Trying second fallback service (ipinfo.io)...');
        return this.getLocationWithFetch('https://ipinfo.io/json', 'second fallback')
            .pipe(
                timeout(this.TIMEOUT_MS),
                map(response => {
                    console.log('Second fallback service response:', response);
                    const locationData: GeolocationData = {
                        country: response.country,
                        country_code: response.country,
                        country_name: response.country,
                        city: response.city,
                        region: response.region,
                        ip: response.ip
                    };
                    this.cachedLocation = locationData;
                    console.log('Second fallback - processed location:', locationData);
                    return locationData;
                }),
                catchError((error) => {
                    console.error('Second fallback location service failed:', error);
                    return this.getDefaultLocation();
                })
            );
    }

    /**
     * Default location when all services fail
     */
    private getDefaultLocation(): Observable<GeolocationData> {
        console.warn('All location services failed. Unable to determine location.');

        // Return unknown location when services fail
        // This will trigger the error handling in the component
        const defaultLocation: GeolocationData = {
            country: 'Unknown',
            country_code: 'XX',
            country_name: 'Unknown Country',
            city: 'Unknown',
            region: 'Unknown',
            ip: 'Unknown'
        };

        this.cachedLocation = defaultLocation;
        return of(defaultLocation);
    }    /**
     * Check if the visitor is from Saudi Arabia
     */
    isSaudiArabia(): Observable<boolean> {
        return this.getCountryByIP().pipe(
            map(location => location.country_code === 'SA')
        );
    }

    /**
     * Get country from browser locale as additional fallback
     */
    getCountryFromLocale(): string {
        const locale = navigator.language || (navigator.languages && navigator.languages[0]) || 'en-US';
        return locale.split('-')[1] || 'US';
    }

    /**
     * Try to get location using browser's geolocation API
     */
    getBrowserGeolocation(): Observable<GeolocationData> {
        return new Observable(observer => {
            // Check if we're on localhost/HTTP - browsers often block geolocation on non-HTTPS
            const isLocalhost = window.location.hostname === 'localhost' ||
                window.location.hostname === '127.0.0.1' ||
                window.location.protocol === 'http:';

            if (isLocalhost) {
                console.warn('Geolocation may not work on localhost/HTTP. Consider using HTTPS or IP detection fallback.');
            }

            if (!navigator.geolocation) {
                observer.error('Geolocation not supported by this browser');
                return;
            }

            // Check if geolocation is available and not blocked
            if (!navigator.permissions) {
                console.warn('Permissions API not supported, proceeding with geolocation request');
            } else {
                // Check permission status first
                navigator.permissions.query({ name: 'geolocation' }).then(permissionStatus => {
                    console.log('Geolocation permission status:', permissionStatus.state);
                    if (permissionStatus.state === 'denied') {
                        observer.error('Location permission was previously denied. Please enable it in browser settings.');
                        return;
                    }
                });
            }

            // Set up a timeout to prevent hanging
            const timeoutId = setTimeout(() => {
                console.error('Geolocation request timed out after 10 seconds');
                observer.error('Location request timed out - this may be due to localhost/HTTP restrictions');
            }, 10000); // 10 second timeout

            console.log('Requesting browser geolocation...');
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    console.log('✅ Geolocation success:', {
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                        accuracy: position.coords.accuracy,
                        timestamp: new Date(position.timestamp).toISOString()
                    });
                    clearTimeout(timeoutId);
                    const lat = position.coords.latitude;
                    const lng = position.coords.longitude;

                    console.log(`Starting reverse geocoding for: ${lat}, ${lng}`);
                    // Use reverse geocoding to get country from coordinates
                    this.reverseGeocode(lat, lng).subscribe({
                        next: (locationData) => {
                            console.log('✅ Complete location detection successful:', locationData);
                            observer.next(locationData);
                        },
                        error: (error) => {
                            console.error('❌ Reverse geocoding failed after successful geolocation:', error);
                            observer.error('Reverse geocoding failed: ' + error);
                        }
                    });
                },
                (error) => {
                    clearTimeout(timeoutId);
                    console.error('❌ Browser geolocation failed:', {
                        code: error.code,
                        message: error.message,
                        timestamp: new Date().toISOString()
                    });

                    let errorMessage = 'Location access failed';
                    let errorDetails = '';

                    switch (error.code) {
                        case 1: // PERMISSION_DENIED
                            errorMessage = isLocalhost
                                ? 'Location access denied. Note: Many browsers block geolocation on localhost/HTTP for security reasons.'
                                : 'Location access denied by user. Please enable location access in your browser settings.';
                            errorDetails = 'User clicked "Don\'t Allow" or browser policy blocked the request';
                            break;
                        case 2: // POSITION_UNAVAILABLE
                            errorMessage = 'Location information unavailable. Your device may not support location services.';
                            errorDetails = 'GPS/location services might be disabled or unavailable';
                            break;
                        case 3: // TIMEOUT
                            errorMessage = 'Location request timed out. Please try again.';
                            errorDetails = 'Location service took too long to respond';
                            break;
                        default:
                            errorMessage = isLocalhost
                                ? 'Location access failed. This may be due to localhost/HTTP security restrictions.'
                                : 'Location access failed. Please check your browser settings.';
                            errorDetails = 'Unknown geolocation error';
                    }

                    console.error('Geolocation error details:', {
                        code: error.code,
                        message: error.message,
                        errorMessage: errorMessage,
                        errorDetails: errorDetails,
                        isLocalhost: isLocalhost,
                        protocol: window.location.protocol,
                        hostname: window.location.hostname,
                        userAgent: navigator.userAgent
                    });

                    observer.error(errorMessage);
                },
                {
                    timeout: 8000, // Browser-level timeout
                    enableHighAccuracy: false, // Set to false for better compatibility on localhost
                    maximumAge: 300000 // 5 minutes cache
                }
            );
        });
    }

    /**
     * Reverse geocode coordinates to get country information using native fetch to bypass interceptors
     */
    private reverseGeocode(lat: number, lng: number): Observable<GeolocationData> {
        console.log('Starting reverse geocoding for coordinates:', lat, lng);
        const url = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`;

        return new Observable(observer => {
            // Use native fetch to bypass Angular HTTP interceptors
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

            fetch(url, {
                signal: controller.signal,
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                }
            })
                .then(response => {
                    clearTimeout(timeoutId);
                    console.log('Reverse geocoding response status:', response.status);
                    if (!response.ok) {
                        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                    }
                    return response.json();
                })
                .then(data => {
                    console.log('Reverse geocoding response:', data);
                    const locationData: GeolocationData = {
                        country: data.countryName,
                        country_code: data.countryCode,
                        country_name: data.countryName,
                        city: data.city || 'Unknown',
                        region: data.principalSubdivision || 'Unknown',
                        ip: 'Browser Location'
                    };
                    console.log('Processed reverse geocoding data:', locationData);
                    observer.next(locationData);
                    observer.complete();
                })
                .catch(error => {
                    clearTimeout(timeoutId);
                    console.error('Reverse geocoding failed:', error);
                    // Fallback to a basic location data using browser locale as hint
                    const countryFromLocale = this.getCountryFromLocale();
                    const fallbackData: GeolocationData = {
                        country: countryFromLocale,
                        country_code: countryFromLocale,
                        country_name: `Location detected (${countryFromLocale})`,
                        city: 'Browser Location',
                        region: 'Browser Location',
                        ip: 'Browser Location - Geocoding Failed'
                    };
                    console.log('Using fallback location data:', fallbackData);
                    observer.next(fallbackData);
                    observer.complete();
                });
        });
    }

    /**
     * Clear cached location data
     */
    clearCache(): void {
        this.cachedLocation = null;
        localStorage.removeItem('manual_location_override');
    }

    /**
     * Manually set location (for cases where auto-detection fails)
     */
    setManualLocation(countryCode: string, countryName: string): void {
        const manualLocation: GeolocationData = {
            country: countryName,
            country_code: countryCode,
            country_name: countryName,
            city: 'Manual Override',
            region: 'Manual Override',
            ip: 'Manual Override'
        };

        this.cachedLocation = manualLocation;
        localStorage.setItem('manual_location_override', JSON.stringify(manualLocation));
        console.log('Manual location set:', manualLocation);
    }

    /**
     * Get manual location override if exists
     */
    private getManualLocationOverride(): GeolocationData | null {
        const stored = localStorage.getItem('manual_location_override');
        if (stored) {
            try {
                return JSON.parse(stored);
            } catch (e) {
                console.error('Failed to parse manual location override:', e);
                localStorage.removeItem('manual_location_override');
            }
        }
        return null;
    }

    /**
     * Force refresh location data (clears cache and fetches new data)
     */
    refreshLocation(): Observable<GeolocationData> {
        this.clearCache();
        return this.getCountryByIP();
    }

    /**
     * Test method to check what IP is being seen by different services - using fetch to bypass interceptors
     */
    testAllServices(): void {
        console.log('=== TESTING ALL GEOLOCATION SERVICES ===');

        // Test primary service
        fetch('https://ipapi.co/json/')
            .then(response => response.json())
            .then(data => {
                console.log('🔵 PRIMARY (ipapi.co):', {
                    ip: data.ip,
                    country: data.country,
                    country_code: data.country_code,
                    country_name: data.country_name,
                    city: data.city
                });
            })
            .catch(error => console.error('🔴 PRIMARY failed:', error));

        // Test fallback service
        fetch('https://ip-api.com/json/')
            .then(response => response.json())
            .then(data => {
                console.log('🟡 FALLBACK (ip-api.com):', {
                    ip: data.query,
                    country: data.country,
                    country_code: data.countryCode,
                    city: data.city
                });
            })
            .catch(error => console.error('🔴 FALLBACK failed:', error));

        // Test second fallback
        fetch('https://ipinfo.io/json')
            .then(response => response.json())
            .then(data => {
                console.log('🟢 SECOND FALLBACK (ipinfo.io):', {
                    ip: data.ip,
                    country: data.country,
                    city: data.city
                });
            })
            .catch(error => console.error('🔴 SECOND FALLBACK failed:', error));

        // Test a fourth service for comparison
        fetch('https://api.ipgeolocation.io/ipgeo?apiKey=ae0aeb43b8ce42b5998f4a00e988eb70')
            .then(response => response.json())
            .then(data => {
                console.log('🟣 ADDITIONAL TEST (ipgeolocation.io):', {
                    ip: data.ip,
                    country_name: data.country_name,
                    country_code2: data.country_code2,
                    city: data.city
                });
            })
            .catch(error => console.error('🔴 ADDITIONAL TEST failed:', error));
    }

    /**
     * Test API connectivity for debugging
     */
    testApiConnectivity() {
        console.log('=== TESTING API CONNECTIVITY ===');
        console.log('ℹ️ Using native fetch to bypass Angular HTTP interceptors');

        const apis = [
            'https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=24.7136&longitude=46.6753&localityLanguage=en',
            'https://ipapi.co/json/',
            'https://ipinfo.io/json',
            'https://api.ipgeolocation.io/ipgeo?apiKey=ae0aeb43b8ce42b5998f4a00e988eb70'
        ];

        apis.forEach((url, index) => {
            console.log(`Testing API ${index + 1}: ${url}`);

            fetch(url)
                .then(response => {
                    console.log(`✅ API ${index + 1} Response Status:`, response.status);
                    return response.json();
                })
                .then(data => {
                    console.log(`✅ API ${index + 1} Data:`, data);
                })
                .catch(error => {
                    console.error(`❌ API ${index + 1} Failed:`, error);
                    console.log(`💡 Check if ${url} is accessible from your network`);
                });
        });

        // Test basic network connectivity
        console.log('Testing basic network connectivity...');
        fetch('https://www.google.com', { mode: 'no-cors' })
            .then(() => {
                console.log('✅ Basic internet connectivity works');
            })
            .catch(error => {
                console.error('❌ Basic internet connectivity failed:', error);
            });
    }

    /**
     * Test interceptor bypass specifically
     */
    testInterceptorBypass() {
        console.log('=== TESTING INTERCEPTOR BYPASS ===');

        const testUrl = 'https://ipapi.co/json/';

        // Test with Angular HttpClient (will go through interceptor)
        console.log('1. Testing with Angular HttpClient (may be intercepted):');
        this.http.get<any>(testUrl).subscribe({
            next: (response) => {
                console.log('✅ HttpClient request succeeded:', response);
            },
            error: (error) => {
                console.error('❌ HttpClient request failed:', error);
                console.log('This failure indicates the interceptor is blocking external API calls');
            }
        });

        // Test with native fetch (bypasses interceptor)
        console.log('2. Testing with native fetch (bypasses interceptor):');
        fetch(testUrl)
            .then(response => {
                console.log('✅ Fetch response status:', response.status);
                return response.json();
            })
            .then(data => {
                console.log('✅ Fetch request succeeded:', data);
            })
            .catch(error => {
                console.error('❌ Fetch request failed:', error);
            });
    }
}
